import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, ChevronRight, Loader2, X, Minus } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { api, ApiError } from '../lib/api';
import { useSeo } from '../lib/seo';

type Alternative = {
  slug: string;
  competitor: string;
  title: string;
  description: string;
  h1: string;
  intro: string[];
  pricing: { zettapost: string; them: string; note: string };
  savings: { headline: string; sub: string };
  comparison: { feature: string; zettapost: Cell; them: Cell }[];
  reasons: { title: string; body: string }[];
  faqs: { q: string; a: string }[];
};

type Cell = { v: 'yes' | 'no' | 'partial'; note?: string };
type ArticleSection = { heading: string; paragraphs: string[] };

function CmpCell({ cell }: { cell: Cell }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {cell.v === 'yes' && <Check size={16} className="text-emerald-600 shrink-0" />}
      {cell.v === 'no' && <X size={16} className="text-slate-300 shrink-0" />}
      {cell.v === 'partial' && <Minus size={16} className="text-amber-500 shrink-0" />}
      {cell.note && <span className="text-xs text-slate-400">{cell.note}</span>}
    </span>
  );
}

export function AlternativePage() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['alternatives', slug],
    queryFn: () => api<{ alternative: Alternative; article: ArticleSection[] }>(`/api/alternatives/${slug}`),
    retry: false,
  });
  const alt = data?.alternative;
  const article = data?.article ?? [];

  useSeo({
    title: alt?.title ?? 'Alternativas · Zettapost',
    description: alt?.description,
    canonical: `https://zettapost.com/alternativas/${slug}`,
  });

  // Unknown slug → back to the hub.
  if (error instanceof ApiError && error.status === 404) return <Navigate to="/alternativas" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {isLoading || !alt ? (
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 size={26} className="animate-spin" />
        </div>
      ) : (
        <>
          <section className="bg-gradient-to-br from-white via-brand-50/40 to-white border-b border-slate-200">
            <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
              <nav className="flex items-center gap-1 text-xs text-slate-400 mb-4">
                <Link to="/" className="hover:text-slate-600">Inicio</Link>
                <ChevronRight size={12} />
                <Link to="/alternativas" className="hover:text-slate-600">Alternativas</Link>
                <ChevronRight size={12} />
                <span className="text-slate-500">{alt.competitor}</span>
              </nav>
              <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{alt.h1}</h1>
              <div className="mt-5 space-y-3">
                {alt.intro.map((p, i) => (
                  <p key={i} className="text-lg text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
              <Link
                to="/register"
                className="btn-accent mt-7 inline-flex text-base px-6 py-3 gap-2 group rounded-xl"
              >
                Prueba Zettapost gratis
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
            </div>
          </section>

          <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 lg:py-16 space-y-14">
            {/* Savings banner */}
            <section className="rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 text-white px-6 py-9 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                Zettapost vs {alt.competitor}
              </p>
              <p className="mt-2 text-2xl sm:text-3xl font-extrabold">{alt.savings.headline}</p>
              <p className="mt-2 text-white/90">{alt.savings.sub}</p>
            </section>

            {/* Pricing */}
            <section>
              <h2 className="text-2xl font-bold mb-5">Precio: Zettapost vs {alt.competitor}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border-2 border-brand-500 p-5">
                  <p className="text-sm font-bold text-brand-600">Zettapost</p>
                  <p className="mt-1.5 text-lg font-bold text-slate-900">{alt.pricing.zettapost}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 p-5">
                  <p className="text-sm font-bold text-slate-500">{alt.competitor}</p>
                  <p className="mt-1.5 text-lg font-bold text-slate-600">{alt.pricing.them}</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-400">{alt.pricing.note}</p>
            </section>

            {/* Comparison table */}
            <section>
              <h2 className="text-2xl font-bold mb-5">Comparativa: Zettapost vs {alt.competitor}</h2>
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left font-medium text-slate-400 px-4 py-3"></th>
                      <th className="text-left font-bold text-brand-700 px-4 py-3">Zettapost</th>
                      <th className="text-left font-medium text-slate-500 px-4 py-3">{alt.competitor}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alt.comparison.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="px-4 py-3 font-medium text-slate-700">{r.feature}</td>
                        <td className="px-4 py-3"><CmpCell cell={r.zettapost} /></td>
                        <td className="px-4 py-3"><CmpCell cell={r.them} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Reasons */}
            <section>
              <h2 className="text-2xl font-bold mb-5">Por qué elegir Zettapost</h2>
              <div className="space-y-3">
                {alt.reasons.map((r, i) => (
                  <div key={i} className="flex gap-3 rounded-xl border border-slate-200 p-4">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                      <Check size={14} />
                    </span>
                    <p className="text-slate-600 leading-relaxed">
                      <strong className="text-slate-900">{r.title}.</strong> {r.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Long-form article */}
            {article.length > 0 && (
              <section>
                {article.map((s, i) => (
                  <div key={i} className="mb-8 last:mb-0">
                    <h2 className="text-2xl font-bold mb-3">{s.heading}</h2>
                    {s.paragraphs.map((p, j) => (
                      <p key={j} className="text-slate-600 leading-relaxed mb-3 last:mb-0">{p}</p>
                    ))}
                  </div>
                ))}
              </section>
            )}

            {/* FAQ */}
            <section>
              <h2 className="text-2xl font-bold mb-5">Preguntas frecuentes</h2>
              <div className="space-y-3">
                {alt.faqs.map((f, i) => (
                  <details key={i} className="group rounded-xl border border-slate-200 open:shadow-sm">
                    <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-slate-900">
                      {f.q}
                    </summary>
                    <p className="px-4 pb-4 -mt-1 text-slate-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* Final CTA */}
            <section className="text-center rounded-2xl bg-slate-50 border border-slate-200 px-6 py-10">
              <h2 className="text-2xl font-bold">¿Te pasas a Zettapost?</h2>
              <p className="mt-2 text-slate-600">Pruébalo 3 días gratis. Cancela cuando quieras.</p>
              <Link to="/register" className="btn-accent mt-6 inline-flex text-base px-7 py-3 gap-2 group rounded-xl">
                Crear cuenta
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
            </section>

            <p className="text-xs text-slate-400 text-center">
              Comparativa informativa basada en información pública; las funciones y precios de {alt.competitor}{' '}
              pueden cambiar. Consulta su web para los datos actuales.
            </p>
          </div>
        </>
      )}

      <SiteFooter />
    </div>
  );
}
